import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useUpdateconsumfinalMutation, useGetconsumfinalQuery } from '../../../../features/consumfinal/consumfinalApiSlice';
import { useGetconsummasterQuery } from '../../../../features/consummaster/consummasterApiSlice';
import { useGetpartnumberQuery } from '../../../../features/partnumber/partnumberApiSlice';
import { useGetModelNumbersQuery } from '../../../../features/modelnumber/modelNumberApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import ConsumablesTable from '../ConsumablesTable';

// Styled components defined outside the component for better performance
const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ForwardButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const ReturnButton = styled(StyledButton)`
  background-color: #ff9800;
`;

const RejectButton = styled(StyledButton)`
  background-color: #f44336;
`;

const IssueButton = styled(StyledButton)`
  background-color: #2196f3;
`;

const CancelButton = styled(StyledButton)`
  background-color: #9e9e9e;
`;

const ConfirmButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const ActionButton = styled(StyledButton)`
  background-color: #1976d2;
`;

const ClearButton = styled(Button)`
  margin-right: auto;
  background-color: #f44336;
  color: white;
  &:hover {
    background-color: #d32f2f;
  }
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
  margin-top: 20px;
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const SummaryContainer = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 5px 10px;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const SummaryItemName = styled.span`
  font-weight: 500;
  margin-right: 10px;
`;

const SummaryItemCount = styled.span`
  background-color: #1976d2;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
`;

const NotificationMessage = styled.div`
  margin-top: 10px;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  background-color: ${props => props.isPartial ? '#ff9800' : '#4caf50'};
  color: white;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorCloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 0 5px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingText = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  font-weight: bold;
`;


// Memoized summary component
const ConsumablesSummary = React.memo(({ items, generateSummary }) => (
  <SummaryContainer>
    <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
      Total: {items.reduce((sum, item) => sum + parseInt(item.selectedQuantity), 0)} items
    </span>
    {generateSummary(items).map((item, index) => (
      <SummaryItem key={index}>
        <SummaryItemName>{item.name}</SummaryItemName>
        <SummaryItemCount>{item.count}</SummaryItemCount>
      </SummaryItem>
    ))}
  </SummaryContainer>
));

const StoreL1InboxFormBTS = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [openIssueModal, setOpenIssueModal] = useState(false);
  const [isPartialIssue, setIsPartialIssue] = useState(false);
  const [consumablesToIssue, setConsumablesToIssue] = useState([]);
  const [allSelected, setAllSelected] = useState(true);
  const [selectionConfirmed, setSelectionConfirmed] = useState(false);
  const [confirmedItems, setConfirmedItems] = useState([]);
  const [originalConsumables, setOriginalConsumables] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Auth and office data
  const auth = useAuth();
  const user_current_office = useSelector((state) => state.office.off);
  
  // API queries with better error handling
  const { getSingleuser, isError: isUsersError, error: usersError } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data, isError, error }) => ({
      getSingleuser: data?.entities,
      isError,
      error
    }),
  });

  const { officedata, isError: isOfficesError, error: officesError } = useGetofficesQuery('offices', {
    selectFromResult: ({ data, isError, error }) => ({
      officedata: data?.entities,
      isError,
      error
    }),
  });

  const { storeoffices, isError: isStoreOfficesError, error: storeOfficesError } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data, isError, error }) => ({
      storeoffices: data?.entities,
      isError,
      error
    }),
  });

  // Master data queries for part number selection
  const { consumablemaster } = useGetconsummasterQuery('cosummaster', {
    selectFromResult: ({ data }) => ({
      consumablemaster: data?.entities
    }),
  });

  const { partnumber } = useGetpartnumberQuery('partno', {
    selectFromResult: ({ data }) => ({
      partnumber: data?.entities
    }),
  });

  const { modelnumber } = useGetModelNumbersQuery('modelnumber', {
    selectFromResult: ({ data }) => ({  
      modelnumber: data?.entities
    }),
  });
  
  // Mutations with better error handling
  const [updateConsumableReq, { isLoading: isUpdatingReq, isError: isUpdateError, error: updateError }] = useUpdateConsumableReqMutation();
  const [updateconsumfinal, { isError: isUpdateConsumFinalError, error: updateConsumFinalError }] = useUpdateconsumfinalMutation();

  // Get consumable inventory data
  // Use both consumablesToIssue and confirmedItems to get all part numbers
  const allPartnos = useMemo(() => {
    const fromConsumables = consumablesToIssue.map(item => item.partno).filter(partno => partno);
    const fromConfirmed = confirmedItems.map(item => item.partno).filter(partno => partno);
    // Combine and remove duplicates
    return [...new Set([...fromConsumables, ...fromConfirmed])];
  }, [consumablesToIssue, confirmedItems]);
  
  const { 
    consumfinalData, 
    isError: isConsumFinalError, 
    error: consumFinalError 
  } = useGetconsumfinalQuery({
    partnos: allPartnos,
    store: user_current_office
  }, {
    skip: !allPartnos.length || !user_current_office,
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, isError, error }) => ({
      consumfinalData: data?.entities,
      isError,
      error
    }),
  });

  // Set general error state when any API error occurs
  useEffect(() => {
    if (isUsersError) {
      setError(`Error fetching users: ${usersError?.data?.message || 'Unknown error'}`);
    } else if (isOfficesError) {
      setError(`Error fetching offices: ${officesError?.data?.message || 'Unknown error'}`);
    } else if (isStoreOfficesError) {
      setError(`Error fetching store offices: ${storeOfficesError?.data?.message || 'Unknown error'}`);
    } else if (isUpdateError) {
      setError(`Error updating request: ${updateError?.data?.message || 'Unknown error'}`);
    } else if (isUpdateConsumFinalError) {
      setError(`Error updating inventory: ${updateConsumFinalError?.data?.message || 'Unknown error'}`);
    } else if (isConsumFinalError) {
      setError(`Error fetching inventory data: ${consumFinalError?.data?.message || 'Unknown error'}`);
    } else {
      setError('');
    }
  }, [
    isUsersError, usersError,
    isOfficesError, officesError,
    isStoreOfficesError, storeOfficesError,
    isUpdateError, updateError,
    isUpdateConsumFinalError, updateConsumFinalError,
    isConsumFinalError, consumFinalError
  ]);

  // Get IDs for workflow - memoize to prevent unnecessary recalculations
  const unitHead_id = useMemo(() => 
    officedata ? Object.values(officedata)?.find(office => office?.name === row?.req_data[0]?.sent_by?.office)?.unitHead : "",
    [officedata, user_current_office]
  );
  
  const storeL2_id = useMemo(() => 
    storeoffices ? Object.values(storeoffices).find(item => item?.storeoffice === row?.req_store)?.roleUsers?.L2ReqApprover_C : "",
    [storeoffices, row?.req_store]
  );

  // Get available quantity for a particular partnumber - memoized
  const getAvailableQuantity = useCallback((partno) => {
    if (consumfinalData && partno) {
      // consumfinalData.entities is an object keyed by _id, so we need to search for the partno
      const item = Object.values(consumfinalData).find(entity => entity.partno === partno);
      return item ? item.balance_order_quantity || 0 : 0;
    }
    return 0;
  }, [consumfinalData]);

  // Helper functions
  const filteredPartNumbers = useCallback((assetName, brand, consumable) => {
    if (!modelnumber || !partnumber) return [];
  
    const modelNos = Object.values(modelnumber || {})
      .filter(m => m.asset === assetName && m.brand === brand)
      .map(m => m.modelno);
  
    return Object.values(partnumber || {}).filter(pn =>
      pn.consumable === consumable &&
      (
        pn.assetModelNos.includes(assetName) || // Exact asset name match
        pn.assetModelNos.some(model => modelNos.includes(model)) || // Model number match
        pn.assetModelNos.includes(`All-${assetName}`) || // "All-Asset" match
        pn.assetModelNos.includes(`All-${assetName}-${brand}`) // "All-Asset-Brand" match
      )
    ).map(pn => pn.partno);
  }, [modelnumber, partnumber]);  

  // Get available part numbers for a consumable - memoized
  // Excludes part numbers already selected in other rows to prevent duplicates
  const getPartNumberOptions = useCallback((consumable, asset, assetTag, currentIndex) => {
    console.log(consumable, asset, assetTag);
    if (!partnumber || !modelnumber) return [];
    
    // Get all possible part numbers for this consumable
    let availablePartNumbers = [];
    
    // If asset is "Not Applicable", return all part numbers for this consumable
    if (asset === "Not Applicable" || asset === null) {
      availablePartNumbers = Object.values(partnumber)
        .filter(pn => pn.consumable === consumable)
        .map(pn => pn.partno);
    } else {
      // If asset is specified, filter by asset and model
      availablePartNumbers = filteredPartNumbers(asset, assetTag.value.oem, consumable);
    }
    
    // Filter out part numbers already selected in other rows
    const selectedPartNumbers = consumablesToIssue
      .map((item, idx) => idx !== currentIndex ? item.partno : null)
      .filter(partno => partno !== null && partno !== undefined && partno !== '');
    
    return availablePartNumbers.filter(partno => !selectedPartNumbers.includes(partno));
  }, [partnumber, modelnumber, filteredPartNumbers, consumablesToIssue]);

  // Get the requested quantity for an item from the last consumables_requested object - memoized
  const getRequestedQuantity = useCallback((item) => {
    if (!row?.consumables_requested || row.consumables_requested.length === 0) {
      return item.quantity;
    }
    
    const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
    if (!latestRequest?.data) {
      return item.quantity;
    }
    
    const requestedItem = latestRequest.data.find(reqItem => 
      reqItem.consumable === item.consumable && 
      reqItem.partno === item.partno &&
      (reqItem.assettag?.label === item.assettag?.label)
    );
    
    return requestedItem ? requestedItem.quantity : item.quantity;
  }, [row?.consumables_requested]);
  
  // Generate summary data from selected consumables - memoized
  const generateSummary = useCallback((items) => {
    if (!items || items.length === 0) return [];

    const summary = items.reduce((acc, item) => {
      const { consumable } = item;
      if (!acc[consumable]) {
        acc[consumable] = 0;
      }
      acc[consumable] += parseInt(item.selectedQuantity);
      return acc;
    }, {});

    return Object.entries(summary).map(([name, count]) => ({ name, count }));
  }, []);

  // Initialize consumables from latest request
  useEffect(() => {
    if (row?.consumables_requested && row.consumables_requested.length > 0) {
      const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
      if (latestRequest?.data) {
        const initialConsumables = latestRequest.data.map((item, index) => ({
          ...item,
          groupId: `group-${index}`, // Add groupId to track original requests and their splits
          isSplit: false, // Track if this is a split item
          originalQuantity: parseInt(item.quantity, 10), // Store original requested quantity
          selectedQuantity: item.partno ? item.quantity : 0, // Set to 0 if no part number
          isSelected: true,
          partno: item.partno || null // Ensure partno is properly set
        }));
        setConsumablesToIssue(initialConsumables);
        setOriginalConsumables(latestRequest.data.map((item, index) => ({
          ...item,
          groupId: `group-${index}`,
          isSplit: false,
          originalQuantity: parseInt(item.quantity, 10),
          selectedQuantity: item.partno ? item.quantity : 0,
          isSelected: true,
          partno: item.partno || null
        })));
      }
    }
  }, [row]);

  // Update quantities based on inventory data when available
  useEffect(() => {
    if (consumfinalData && consumablesToIssue.length > 0) {
      setConsumablesToIssue(prev => 
        prev.map(item => {
          // Only update if item has a part number
          if (!item.partno) return item;
          
          const availableQty = getAvailableQuantity(item.partno);
          const requestedQty = parseInt(getRequestedQuantity(item));
          
          // Only adjust quantity if available quantity is lower than requested
          if (availableQty < requestedQty) {
            return {
              ...item,
              selectedQuantity: Math.max(0, availableQty),
              // Preserve the user's selection - don't auto-deselect
              // Let them manually deselect if needed
            };
          }
          return item;
        })
      );
    }
  }, [consumfinalData, getAvailableQuantity, getRequestedQuantity]);

  useEffect(() => {
    // Check if this request was returned from Store L2
    const isReturnedFromL2 = row?.curr_sent_to_tag === "storel2_bts_return_l1";
    
    if (row?.store_consum_issue && row?.store_consum_issue?.length > 0) {
      const latestIssue = row.store_consum_issue[row.store_consum_issue.length - 1];
      setConfirmedItems(latestIssue.data || []);
      
      // If returned from L2, allow editing by not confirming selection
      if (!isReturnedFromL2) {
        setSelectionConfirmed(true);
      } else {
        setSelectionConfirmed(false);
        // Also populate consumablesToIssue with the confirmed items for editing
        const reconstructed = (latestIssue.data || []).map(item => ({
          ...item,
          isSelected: true // All confirmed items should be selected
        }));
        setConsumablesToIssue(reconstructed);
      }
    }
  }, [row]);

  // Check if this is a full or partial selection
  useEffect(() => {
    // Group items by groupId to check totals
    const groupTotals = {};
    const groupRequested = {};
    
    consumablesToIssue.forEach(item => {
      if (!groupTotals[item.groupId]) {
        groupTotals[item.groupId] = 0;
        groupRequested[item.groupId] = item.originalQuantity;
      }
      if (item.isSelected) {
        groupTotals[item.groupId] += parseInt(item.selectedQuantity || 0, 10);
      }
    });
    
    // Check if any group has less than requested (considering splits)
    const isPartial = Object.keys(groupTotals).some(groupId => {
      const totalSelected = groupTotals[groupId];
      const totalRequested = groupRequested[groupId];
      return totalSelected < totalRequested;
    });
    
    setIsPartialIssue(isPartial);
    
    const allItemsSelected = consumablesToIssue.every(item => item.isSelected);
    setAllSelected(allItemsSelected);
    
    const hasSelectedItems = consumablesToIssue.some(item => item.isSelected);
    console.log('Selection state:', {
      totalItems: consumablesToIssue.length,
      selectedItems: consumablesToIssue.filter(item => item.isSelected).length,
      allSelected: allItemsSelected,
      hasSelected: hasSelectedItems,
      groupTotals,
      groupRequested,
      isPartial,
      buttonShouldBeDisabled: consumablesToIssue.every(item => !item.isSelected)
    });
  }, [consumablesToIssue]);

  // Check if all items have zero available quantity - memoized for performance
  const allItemsHaveZeroQuantity = useMemo(() => {
    return consumablesToIssue.length > 0 && consumablesToIssue.every(item => 
      getAvailableQuantity(item.partno) === 0
    );
  }, [consumablesToIssue, getAvailableQuantity]);
  
  // Check if confirmed items selection is partial or full
  useEffect(() => {
    if (confirmedItems.length > 0 && row?.consumables_requested && row.consumables_requested.length > 0) {
      const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
      const requestedItems = latestRequest?.data || [];
      
      // Check if partial issue based on group totals (to handle splits correctly)
      const confirmedGroupTotals = {};
      const requestedGroupTotals = {};
      
      // Calculate totals for confirmed items by group
      confirmedItems.forEach(item => {
        if (!confirmedGroupTotals[item.groupId]) {
          confirmedGroupTotals[item.groupId] = 0;
        }
        confirmedGroupTotals[item.groupId] += parseInt(item.selectedQuantity, 10);
      });
      
      // Calculate totals for requested items by group
      requestedItems.forEach((item, index) => {
        const groupId = `group-${index}`;
        requestedGroupTotals[groupId] = parseInt(item.quantity, 10);
      });
      
      // Check if any group has less total quantity than requested
      const isPartial = Object.keys(requestedGroupTotals).some(groupId => {
        const confirmedTotal = confirmedGroupTotals[groupId] || 0;
        const requestedTotal = requestedGroupTotals[groupId];
        return confirmedTotal < requestedTotal;
      });
      
      setIsPartialIssue(isPartial);
    }
  }, [confirmedItems, row]);

  // Handle opening issue modal
  const handleOpenIssueModal = useCallback(() => {
    setOpenIssueModal(true);
  }, []);

  // Handle closing issue modal
  const handleCloseIssueModal = useCallback(() => {
    setOpenIssueModal(false);
  }, []);

  // Handle reopening the modal to edit selections
  const handleReopenForEdit = useCallback(() => {
    console.log('Reopening for edit...');
    console.log('Original consumables:', originalConsumables);
    console.log('Confirmed items:', confirmedItems);
    
    // Build a map of confirmed items by groupId for easier lookup
    const confirmedByGroup = {};
    confirmedItems.forEach(item => {
      if (!confirmedByGroup[item.groupId]) {
        confirmedByGroup[item.groupId] = [];
      }
      confirmedByGroup[item.groupId].push(item);
    });
    
    // Reconstruct the consumablesToIssue from confirmed items, preserving splits
    if (confirmedItems.length > 0) {
      const reconstructed = confirmedItems.map(item => ({
        ...item,
        isSelected: true // All confirmed items should be selected
      }));
      
      setConsumablesToIssue(reconstructed);
    } else if (originalConsumables.length > 0) {
      // If no confirmed items, reset to original state
      setConsumablesToIssue([...originalConsumables]);
    }
    
    setOpenIssueModal(true);
  }, [originalConsumables, confirmedItems]);

  // Handle toggling all items
  const handleToggleAll = useCallback((event) => {
    const isChecked = event.target.checked;
    setAllSelected(isChecked);
    
    // Update all items to be selected or not
    setConsumablesToIssue(prev => {
      const newItems = prev.map(item => ({
        ...item,
        isSelected: isChecked
      }));
      
      // If all items are selected, check if any quantities are partial
      const isPartial = isChecked ? 
        newItems.some(item => parseInt(item.selectedQuantity) < parseInt(getRequestedQuantity(item))) : 
        true; // If not all selected, it's always partial
      
      setIsPartialIssue(isPartial);
      
      return newItems;
    });
  }, [getRequestedQuantity]);

  // Handle unselecting all items
  const handleUnselectAll = useCallback(() => {
    setAllSelected(false);
    setConsumablesToIssue(prev => 
      prev.map(item => ({
        ...item,
        isSelected: false
      }))
    );
  }, []);

  // Handle clearing all selections
  const handleClearAllSelections = useCallback(() => {
    try {
      // Deselect all items in the current view
      setConsumablesToIssue(prev => 
        prev.map(item => ({
          ...item,
          isSelected: false
        }))
      );
      setAllSelected(false);
      
      // Clear confirmed selections
      setConfirmedItems([]);
      setSelectionConfirmed(false);
      
      // Close the modal
      setOpenIssueModal(false);
      
      // Show message to user
      alert("All items have been deselected.");
    } catch (err) {
      setError(`Failed to clear selections: ${err.message}`);
      console.error("Error clearing selections:", err);
    }
  }, []);

  // Handle toggling individual item
  const handleToggleItem = useCallback((index) => {
    setConsumablesToIssue(prev => {
      const newItems = [...prev];
      newItems[index] = {
        ...newItems[index],
        isSelected: !newItems[index].isSelected
      };
      
      // Check if this is now a partial issue or if all items are selected
      const isPartial = newItems.some(item => 
        !item.isSelected || parseInt(item.selectedQuantity) < parseInt(getRequestedQuantity(item))
      );
      setIsPartialIssue(isPartial);
      
      const allItemsSelected = newItems.every(item => item.isSelected);
      setAllSelected(allItemsSelected);
      
      return newItems;
    });
  }, [getRequestedQuantity]);

  // Get total selected quantity for a group
  const getGroupTotalQuantity = useCallback((groupId) => {
    return consumablesToIssue
      .filter(item => item.groupId === groupId)
      .reduce((sum, item) => sum + (parseInt(item.selectedQuantity, 10) || 0), 0);
  }, [consumablesToIssue]);

  // Get total selected quantity for a group (for confirmed items)
  const getConfirmedGroupTotalQuantity = useCallback((groupId) => {
    return confirmedItems
      .filter(item => item.groupId === groupId)
      .reduce((sum, item) => sum + (parseInt(item.selectedQuantity, 10) || 0), 0);
  }, [confirmedItems]);

  // Handle changing selected quantity
  const handleQuantityChange = useCallback((index, value) => {
    try {
      const item = consumablesToIssue[index];
      let newQuantity = parseInt(value);
      
      // Validate quantity
      if (isNaN(newQuantity) || newQuantity < 0) {
        newQuantity = 0;
      }
      
      // Calculate current group total excluding the current item
      const currentGroupTotal = consumablesToIssue
        .filter((c, i) => c.groupId === item.groupId && i !== index)
        .reduce((sum, c) => sum + (parseInt(c.selectedQuantity, 10) || 0), 0);
      
      // Maximum value is the lower of: original quantity minus other items in group, and available quantity
      const maxByGroup = item.originalQuantity - currentGroupTotal;
      const availableQuantity = getAvailableQuantity(item.partno);
      const maxAllowed = Math.min(maxByGroup, availableQuantity);
      
      // Ensure the value doesn't exceed the maximum allowed
      const validQuantity = Math.min(newQuantity, maxAllowed);
      
      setConsumablesToIssue(prev => {
        const newItems = [...prev];
        newItems[index] = {
          ...newItems[index],
          selectedQuantity: validQuantity
        };
        
        // Check if this is now a partial issue
        const isPartial = newItems.some(item => 
          !item.isSelected || parseInt(item.selectedQuantity) < parseInt(getRequestedQuantity(item))
        );
        setIsPartialIssue(isPartial);
        
        return newItems;
      });
    } catch (err) {
      setError(`Error updating quantity: ${err.message}`);
      console.error("Error handling quantity change:", err);
    }
  }, [consumablesToIssue, getRequestedQuantity, getAvailableQuantity]);

  // Handle quantity increment/decrement
  const handleQuantityAdjust = useCallback((index, amount) => {
    try {
      const item = consumablesToIssue[index];
      const currentValue = parseInt(item.selectedQuantity || 0);
      let newValue = currentValue + amount;
      
      // Calculate current group total excluding the current item
      const currentGroupTotal = consumablesToIssue
        .filter((c, i) => c.groupId === item.groupId && i !== index)
        .reduce((sum, c) => sum + (parseInt(c.selectedQuantity, 10) || 0), 0);
      
      // Maximum value is the lower of: original quantity minus other items in group, and available quantity
      const maxByGroup = item.originalQuantity - currentGroupTotal;
      const availableQuantity = getAvailableQuantity(item.partno);
      const maxAllowed = Math.min(maxByGroup, availableQuantity);
      
      if (newValue < 0) newValue = 0;
      if (newValue > maxAllowed) newValue = maxAllowed;
      
      handleQuantityChange(index, newValue);
    } catch (err) {
      setError(`Error adjusting quantity: ${err.message}`);
      console.error("Error handling quantity adjustment:", err);
    }
  }, [consumablesToIssue, getAvailableQuantity, handleQuantityChange]);

  // Handle part number selection
  const handlePartNumberChange = useCallback((index, newPartNumber) => {
    try {
      setConsumablesToIssue(prev => {
        const newItems = [...prev];
        const item = newItems[index];
        
        // Only auto-fill quantity if this is the first time selecting a part number (partno was null/empty)
        let newSelectedQuantity = item.selectedQuantity;
        
        if (!item.partno && newPartNumber) {
          // This is the first time selecting a part number - auto-fill with remaining quantity
          const currentGroupTotal = newItems
            .filter((c, i) => c.groupId === item.groupId && i !== index)
            .reduce((sum, c) => sum + (parseInt(c.selectedQuantity, 10) || 0), 0);
          
          const remainingQty = item.originalQuantity - currentGroupTotal;
          newSelectedQuantity = remainingQty;
        } else if (!newPartNumber) {
          // Part number is being cleared - reset quantity to 0
          newSelectedQuantity = 0;
        }
        // If changing from one part number to another, keep the existing selectedQuantity
        
        newItems[index] = {
          ...newItems[index],
          partno: newPartNumber,
          selectedQuantity: newSelectedQuantity
        };
        return newItems;
      });
    } catch (err) {
      setError(`Error updating part number: ${err.message}`);
      console.error("Error handling part number change:", err);
    }
  }, []);

  // Handler for splitting a consumable row into multiple rows
  const handleSplitRow = useCallback((index) => {
    const itemToSplit = consumablesToIssue[index];
    
    // Create a new split row with the same properties but reset quantities
    const newSplitRow = {
      ...itemToSplit,
      isSplit: true,
      partno: null, // Reset part number for new split
      selectedQuantity: 0, // Reset selected quantity
      isSelected: true // New split is selected by default
    };
    
    // Insert the new split row after the current item
    const updatedConsumables = [
      ...consumablesToIssue.slice(0, index + 1),
      newSplitRow,
      ...consumablesToIssue.slice(index + 1)
    ];
    
    setConsumablesToIssue(updatedConsumables);
  }, [consumablesToIssue]);

  // Handler for removing a split row
  const handleRemoveSplitRow = useCallback((index) => {
    const itemToRemove = consumablesToIssue[index];
    
    // Only allow removal of split items, not original items
    if (!itemToRemove.isSplit) {
      alert("Cannot remove original request item. You can only remove split rows.");
      return;
    }
    
    // Remove the item at the specified index
    const updatedConsumables = consumablesToIssue.filter((_, i) => i !== index);
    setConsumablesToIssue(updatedConsumables);
  }, [consumablesToIssue]);

  // Handle confirmed quantity change
  const handleConfirmedQuantityChange = useCallback((index, value) => {
    try {
      const item = confirmedItems[index];
      let newQuantity = parseInt(value);
      
      // Validate quantity
      if (isNaN(newQuantity) || newQuantity < 0) {
        newQuantity = 0;
      }
      
      // Calculate current group total excluding the current item
      const currentGroupTotal = confirmedItems
        .filter((c, i) => c.groupId === item.groupId && i !== index)
        .reduce((sum, c) => sum + (parseInt(c.selectedQuantity, 10) || 0), 0);
      
      // Maximum value is the lower of: original quantity minus other items in group, and available quantity
      const maxByGroup = item.originalQuantity - currentGroupTotal;
      const availableQuantity = getAvailableQuantity(item.partno);
      const maxAllowed = Math.min(maxByGroup, availableQuantity);
      
      // Ensure the value doesn't exceed the maximum allowed
      const validQuantity = Math.min(newQuantity, maxAllowed);
      
      setConfirmedItems(prev => {
        const newItems = [...prev];
        newItems[index] = {
          ...newItems[index],
          selectedQuantity: validQuantity
        };
        
        // Get requested items for comparison
        const latestRequest = row?.consumables_requested?.[row.consumables_requested.length - 1];
        const requestedItems = latestRequest?.data || [];
        
        // Check if partial issue based on group totals (to handle splits correctly)
        const confirmedGroupTotals = {};
        const requestedGroupTotals = {};
        
        // Calculate totals for confirmed items by group
        newItems.forEach(item => {
          if (!confirmedGroupTotals[item.groupId]) {
            confirmedGroupTotals[item.groupId] = 0;
          }
          confirmedGroupTotals[item.groupId] += parseInt(item.selectedQuantity, 10);
        });
        
        // Calculate totals for requested items by group
        requestedItems.forEach((item, idx) => {
          const groupId = `group-${idx}`;
          requestedGroupTotals[groupId] = parseInt(item.quantity, 10);
        });
        
        // Check if any group has less total quantity than requested
        const isPartial = Object.keys(requestedGroupTotals).some(groupId => {
          const confirmedTotal = confirmedGroupTotals[groupId] || 0;
          const requestedTotal = requestedGroupTotals[groupId];
          return confirmedTotal < requestedTotal;
        });
        
        setIsPartialIssue(isPartial);
        
        return newItems;
      });
    } catch (err) {
      setError(`Error updating confirmed quantity: ${err.message}`);
      console.error("Error in confirmed quantity change:", err);
    }
  }, [confirmedItems, getRequestedQuantity, getAvailableQuantity, row?.consumables_requested]);

  // Handle quantity adjust in confirmed items
  const handleConfirmedQuantityAdjust = useCallback((index, amount) => {
    try {
      const item = confirmedItems[index];
      const currentValue = parseInt(item.selectedQuantity || 0);
      let newValue = currentValue + amount;
      
      // Calculate current group total excluding the current item
      const currentGroupTotal = confirmedItems
        .filter((c, i) => c.groupId === item.groupId && i !== index)
        .reduce((sum, c) => sum + (parseInt(c.selectedQuantity, 10) || 0), 0);
      
      // Maximum value is the lower of: original quantity minus other items in group, and available quantity
      const maxByGroup = item.originalQuantity - currentGroupTotal;
      const availableQuantity = getAvailableQuantity(item.partno);
      const maxAllowed = Math.min(maxByGroup, availableQuantity);
      
      if (newValue < 0) newValue = 0;
      if (newValue > maxAllowed) newValue = maxAllowed;
      
      handleConfirmedQuantityChange(index, newValue);
    } catch (err) {
      setError(`Error adjusting confirmed quantity: ${err.message}`);
      console.error("Error in confirmed quantity adjustment:", err);
    }
  }, [confirmedItems, getAvailableQuantity, handleConfirmedQuantityChange]);

  // Handle rejecting the request
  const handleRejectRequest = useCallback(async () => {
    if (!remark.trim()) {
      alert("Please provide a remark explaining the reason for rejection.");
      return;
    }
    
    try {
      setIsLoading(true);
      const senttoData = {
        sent_to_tag: "store_bts_rejected",
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: row?.req_creator_id, 
          name: getSingleuser?.[row?.req_creator_id]?.name, 
          office: row?.req_data[0]?.sent_by?.office, 
          designation: row?.req_data[0]?.sent_by?.designation 
        },
        action: "Request rejected by Store Level 1",
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
        curr_status: "Rejected",
        time_stamp: new Date()
      };
      
      const body = { id: row?._id, senttoData: senttoData };
      
      await updateConsumableReq(body).unwrap();
      alert("Request has been rejected.");
      handleBack();
    } catch (err) {
      setError(`Failed to reject request: ${err.message || 'Unknown error'}`);
      console.error("Error rejecting request:", err);
      alert("There was an error rejecting the request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [remark, auth, getSingleuser, user_current_office, row, updateConsumableReq, handleBack]);
  
  // Handle returning to Unit Head for modification
  const handleReturnToUnitHead = useCallback(async () => {
    if (!remark.trim()) {
      alert("Please provide a remark explaining what needs to be modified.");
      return;
    }
    
    try {
      setIsLoading(true);
      const senttoData = {
        sent_to_tag: "store_bts_return_unithead",
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: unitHead_id, 
          name: getSingleuser?.[unitHead_id]?.name, 
          office: row?.req_data[0]?.sent_by?.office, 
          designation: getSingleuser?.[unitHead_id]?.designation?.[row?.req_data[0]?.sent_by?.office] 
        },
        action: "Request returned to Unit Head for modification",
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[unitHead_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[unitHead_id]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
        time_stamp: new Date()
      };
      
      const body = { id: row?._id, senttoData: senttoData };
      
      await updateConsumableReq(body).unwrap();
      alert("Request returned to Unit Head for modification.");
      handleBack();
    } catch (err) {
      setError(`Failed to return request: ${err.message || 'Unknown error'}`);
      console.error("Error returning request:", err);
      alert("There was an error returning the request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [remark, auth, getSingleuser, user_current_office, unitHead_id, row, updateConsumableReq, handleBack]);
 
  // Confirm selection from modal
  const handleConfirmSelection = useCallback(() => {
    try {
      // Filter selected items
      const selectedItems = consumablesToIssue
        .filter(item => item.isSelected)
        .map(item => ({
          ...item,
          quantity: item.quantity,
          selectedQuantity: item.selectedQuantity
        }));

      console.log('Selected items:', selectedItems);
      console.log('All items:', consumablesToIssue);

      if (selectedItems.length === 0) {
        alert("Please select at least one item to issue.");
        setSelectionConfirmed(false);
        return;
      }

      // Check if all selected items have part numbers
      const itemsWithoutPartNo = selectedItems.filter(item => !item.partno);
      if (itemsWithoutPartNo.length > 0) {
        console.log('Items without part numbers:', itemsWithoutPartNo);
        alert(`Please select part numbers for all selected items before confirming. Missing part numbers for: ${itemsWithoutPartNo.map(item => item.consumable).join(', ')}`);
        return;
      }

      // Check if all selected items have valid quantities
      const itemsWithoutQuantity = selectedItems.filter(item => !item.selectedQuantity || parseInt(item.selectedQuantity) <= 0);
      if (itemsWithoutQuantity.length > 0) {
        console.log('Items without valid quantities:', itemsWithoutQuantity);
        alert(`Please enter valid quantities for all selected items. Missing quantities for: ${itemsWithoutQuantity.map(item => item.consumable).join(', ')}`);
        return;
      }

      // Validate that total quantities per group don't exceed requested
      const groupValidation = {};
      
      selectedItems.forEach(item => {
        if (!groupValidation[item.groupId]) {
          groupValidation[item.groupId] = {
            originalQuantity: item.originalQuantity,
            totalSelected: 0,
            consumableName: item.consumable
          };
        }
        groupValidation[item.groupId].totalSelected += parseInt(item.selectedQuantity, 10) || 0;
      });
      
      // Check if any group exceeds the original quantity
      for (const groupId in groupValidation) {
        const { originalQuantity, totalSelected, consumableName } = groupValidation[groupId];
        if (totalSelected > originalQuantity) {
          alert(`Total selected quantity (${totalSelected}) exceeds requested quantity (${originalQuantity}) for ${consumableName}. Please adjust the quantities.`);
          return;
        }
      }

      // Set confirmed items to just the selected ones
      setConfirmedItems(selectedItems);
      setSelectionConfirmed(true);
      
      // Keep all items in consumablesToIssue (don't filter out unselected ones)
      // This ensures we can show all items again when editing
      
      setOpenIssueModal(false);

      // Get all requested items for comparison
      const latestRequest = row?.consumables_requested?.[row.consumables_requested.length - 1];
      const requestedItems = latestRequest?.data || [];
      
      // Check if partial issue based on group totals (to handle splits correctly)
      const selectedGroupTotals = {};
      const requestedGroupTotals = {};
      
      // Calculate totals for selected items by group
      selectedItems.forEach(item => {
        if (!selectedGroupTotals[item.groupId]) {
          selectedGroupTotals[item.groupId] = 0;
        }
        selectedGroupTotals[item.groupId] += parseInt(item.selectedQuantity, 10);
      });
      
      // Calculate totals for requested items by group
      requestedItems.forEach((item, index) => {
        const groupId = `group-${index}`;
        requestedGroupTotals[groupId] = parseInt(item.quantity, 10);
      });
      
      // Check if any group has less total quantity than requested
      const isPartial = Object.keys(requestedGroupTotals).some(groupId => {
        const selectedTotal = selectedGroupTotals[groupId] || 0;
        const requestedTotal = requestedGroupTotals[groupId];
        return selectedTotal < requestedTotal;
      });
      
      setIsPartialIssue(isPartial);
    } catch (err) {
      setError(`Failed to confirm selection: ${err.message}`);
      console.error("Error confirming selection:", err);
    }
  }, [consumablesToIssue, row?.consumables_requested, getRequestedQuantity]);

  // Process issue of consumables and forward to L2
  const handleForwardToL2 = useCallback(async () => {
    if (!remark.trim()) {
      alert("Please provide a remark before issuing consumables.");
      return;
    }

    if (!storeL2_id) {
      alert("Store Level 2 approver information not found.");
      return;
    }

    if (confirmedItems.length === 0) {
      alert("Please select at least one item to issue.");
      return;
    }

    try {
      setIsLoading(true);
      
      // Check if this request was returned from Store L2 and if we're making changes
      const isReturnedFromL2 = row?.req_data?.some(data => 
        data.sent_to_tag === "storel2_bts_return_l1"
      );
      
      // Get previous issue data if it exists
      const hasPreviousIssueData = row?.store_consum_issue && row.store_consum_issue.length > 0;
      let previousIssueData = null;
      
      if (hasPreviousIssueData) {
        previousIssueData = row.store_consum_issue[row.store_consum_issue.length - 1];
      }
      
      // Check if current selection is identical to previous one
      const selectionUnchanged = hasPreviousIssueData && previousIssueData?.data && confirmedItems.length === previousIssueData.data.length && 
        confirmedItems.every(currItem => {
          return previousIssueData.data.some(prevItem => 
            currItem.consumable === prevItem.consumable &&
            currItem.partno === prevItem.partno &&
            (currItem.assettag?.label === prevItem.assettag?.label) &&
            parseInt(currItem.selectedQuantity) === parseInt(prevItem.quantity || prevItem.selectedQuantity)
          );
        });

      // Create issue data object only if selection is changed or this is first time sending
      const issueData = {
        time_stamp: new Date(),
        status: "draft",
        data: confirmedItems.map(item => ({
          ...item,
          quantity: item.selectedQuantity
        }))
      };

      // Determine if this is a partial or full issue
      const sent_to_tag = isPartialIssue
        ? "storel1_bts_partialissue_l2"
        : "storel1_bts_fullissue_l2";
      
      const action = isPartialIssue
        ? "Partial consumables issued and forwarded to Store Level 2 for approval"
        : "All consumables issued and forwarded to Store Level 2 for approval";
      
      // Create the sent to data object
      const senttoData = {
        sent_to_tag,
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: storeL2_id, 
          name: getSingleuser?.[storeL2_id]?.name, 
          office: user_current_office, 
          designation: getSingleuser?.[storeL2_id]?.designation?.[user_current_office] 
        },
        action,
        // Only include store_consum_issue if there are changes
        ...(isReturnedFromL2 && selectionUnchanged ? {} : { store_consum_issue: issueData }),
        curr_sent_to: storeL2_id, // Add this field to properly route to Store L2
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeL2_id])}-${user_current_office}-${getSingleuser?.[storeL2_id]?.designation?.[user_current_office] || 'Unknown'}`,
        curr_status: "Pending", // Ensure status remains pending
        time_stamp: new Date()
      };
      const body = { id: row?._id, senttoData: senttoData };
      
      // Handle inventory updates based on whether this is a new issue or a modification
      if (!isReturnedFromL2 || !selectionUnchanged) {
        if (isReturnedFromL2 && !selectionUnchanged) {
          // This is a modification - need to adjust based on differences
          console.log('Modifying existing issue - calculating differences');
          
          // Create maps for easy lookup - use a delimiter that won't appear in the data
          const previousItemsMap = new Map();
          previousIssueData.data.forEach(item => {
            const key = `${item.consumable}|||${item.partno}|||${item.assettag?.label}`;
            previousItemsMap.set(key, parseInt(item.quantity || item.selectedQuantity, 10));
          });
          
          const currentItemsMap = new Map();
          confirmedItems.forEach(item => {
            const key = `${item.consumable}|||${item.partno}|||${item.assettag?.label}`;
            const existingQty = currentItemsMap.get(key) || 0;
            currentItemsMap.set(key, existingQty + parseInt(item.selectedQuantity, 10));
          });
          
          // Calculate differences and update inventory
          const allKeys = new Set([...previousItemsMap.keys(), ...currentItemsMap.keys()]);
          
          for (const key of allKeys) {
            const [consumable, partno] = key.split('|||');
            const previousQty = previousItemsMap.get(key) || 0;
            const currentQty = currentItemsMap.get(key) || 0;
            const difference = currentQty - previousQty;
            
            if (difference !== 0) {
              console.log(`Adjusting ${consumable} (${partno}): Previous=${previousQty}, Current=${currentQty}, Diff=${difference}`);
              
              // Update inventory based on difference
              await updateconsumfinal({
                consumable: consumable,
                partno: partno,
                store: user_current_office,
                balanceQuantity: -difference, // If diff is positive (increase), decrease balance; if negative (decrease), increase balance
                isInTransit: difference  // If diff is positive (increase), increase in-transit; if negative (decrease), decrease in-transit
              }).unwrap();
            }
          }
        } else {
          // This is the first time sending - move items from balance to in-transit
          console.log('First time creating issue');
          await Promise.all(confirmedItems.map(async (item) => {
            await updateconsumfinal({
              consumable: item.consumable,
              partno: item.partno,
              store: user_current_office,
              balanceQuantity: -parseInt(item.selectedQuantity), // Decrease balance
              isInTransit: parseInt(item.selectedQuantity)  // Increase in-transit
            }).unwrap();
          }));
        }
      }
      
      // Update the request status
      await updateConsumableReq(body).unwrap();
      
      const responseMessage = isReturnedFromL2 && selectionUnchanged 
        ? "Request forwarded back to Store Level 2 without changes." 
        : `${isPartialIssue ? 'Partial' : 'Full'} consumables issue created and forwarded to Store Level 2 for approval.${isPartialIssue ? ' Some requested items or quantities were not included in this issue.' : ''}`;
      
      alert(responseMessage);
      handleBack();
    } catch (err) {
      setError(`Failed to forward to L2: ${err.message || 'Unknown error'}`);
      console.error("Error creating issue:", err);
      alert("There was an error creating the consumables issue. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    remark, storeL2_id, confirmedItems, row, isPartialIssue,
    auth, getSingleuser, user_current_office,
    updateconsumfinal, updateConsumableReq, handleBack
  ]);

  return (
    <>
      {error && (
        <ErrorMessage>
          {error}
          <ErrorCloseButton onClick={() => setError('')}>×</ErrorCloseButton>
        </ErrorMessage>
      )}
      
      {isLoading && (
        <LoadingOverlay>
          <LoadingText>Processing request...</LoadingText>
        </LoadingOverlay>
      )}

      <RemarkContainer>
        <TextField
          fullWidth
          label="Remarks"
          variant="outlined"
          multiline
          rows={4}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Add your remarks here..."
        />
      </RemarkContainer>
      
      {/* Show confirmed items if selections have been confirmed OR if returned from L2 with confirmed items */}
      {(selectionConfirmed || (row?.curr_sent_to_tag === "storel2_bts_return_l1" && confirmedItems.length > 0)) && confirmedItems.length > 0 && (
        <SelectedAssetsContainer>
          <SelectedAssetsTitleContainer>
            <SelectedAssetsTitle>Selected Consumables for Issue</SelectedAssetsTitle>
            <ActionButtonsContainer>
              <Tooltip title="Edit selections">
                <IconButton color="primary" onClick={handleReopenForEdit} disabled={isLoading}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </ActionButtonsContainer>
          </SelectedAssetsTitleContainer>

          {/* Summary section */}
          <ConsumablesSummary items={confirmedItems} generateSummary={generateSummary} />
           
          <StyledTableContainer component={Paper}>
            <ConsumablesTable 
              items={confirmedItems}
              availableQuantity={getAvailableQuantity}
              getRequestedQuantity={getRequestedQuantity}
              handleToggleItem={() => {}}
              handleQuantityChange={handleConfirmedQuantityChange}
              handleQuantityAdjust={handleConfirmedQuantityAdjust}
              handlePartNumberChange={null}
              getPartNumberOptions={null}
              isSelectionView={false}
              getGroupTotalQuantity={getConfirmedGroupTotalQuantity}
            />
          </StyledTableContainer>
          
          <NotificationMessage isPartial={isPartialIssue}>
            {isPartialIssue 
              ? 'Warning: Partial items selected. Some requested items or quantities will not be issued.' 
              : 'All requested items selected for issue with full quantities.'}
          </NotificationMessage>
        </SelectedAssetsContainer>
      )}
      
      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {!selectionConfirmed || confirmedItems.length === 0 ? (
          // Check if returned from L2 - show Resubmit button instead of Create Issue
          row?.curr_sent_to_tag === "storel2_bts_return_l1" && confirmedItems.length > 0 ? (
            <>
              <ForwardButton onClick={handleForwardToL2} disabled={isLoading}>
                Resubmit to Level 2
              </ForwardButton>
              
              <ReturnButton onClick={handleReturnToUnitHead} disabled={isLoading}>
                Return to Unit Head
              </ReturnButton>
              
              <RejectButton onClick={handleRejectRequest} disabled={isLoading}>
                Reject Request
              </RejectButton>
            </>
          ) : (
            <>
              <IssueButton onClick={handleOpenIssueModal} disabled={isLoading}>
                Create Issue
              </IssueButton>
              
              <ReturnButton onClick={handleReturnToUnitHead} disabled={isLoading}>
                Return to Unit Head
              </ReturnButton>
              
              <RejectButton onClick={handleRejectRequest} disabled={isLoading}>
                Reject Request
              </RejectButton>
            </>
          )
        ) : (
          // Check if returned from L2 - show Resubmit even after confirmation
          row?.curr_sent_to_tag === "storel2_bts_return_l1" ? (
            <>
              <ForwardButton onClick={handleForwardToL2} disabled={isLoading}>
                Resubmit to Level 2
              </ForwardButton>
              
              <ReturnButton onClick={handleReturnToUnitHead} disabled={isLoading}>
                Return to Unit Head
              </ReturnButton>
            </>
          ) : (
            <>
              <ForwardButton onClick={handleForwardToL2} disabled={isLoading}>
                Forward to Level 2
              </ForwardButton>
              
              <ReturnButton onClick={handleReturnToUnitHead} disabled={isLoading}>
                Return to Unit Head
              </ReturnButton>
            </>
          )
        )}
      </div>

      {/* Modal for Create Issue */}
      <Modal
        open={openIssueModal}
        onClose={handleCloseIssueModal}
        aria-labelledby="issue-modal-title"
      >
        <Box sx={ModalStyle}>
          <ModalTitle id="issue-modal-title">Create Consumables Issue</ModalTitle>
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={allSelected} 
                onChange={handleToggleAll}
                disabled={allItemsHaveZeroQuantity}
              />
            }
            label="Select All Items"
            disabled={allItemsHaveZeroQuantity}
          />
          
          <StyledTableContainer component={Paper}>
            <ConsumablesTable 
              items={consumablesToIssue}
              availableQuantity={getAvailableQuantity}
              getRequestedQuantity={getRequestedQuantity}
              handleToggleItem={handleToggleItem}
              handleQuantityChange={handleQuantityChange}
              handleQuantityAdjust={handleQuantityAdjust}
              handlePartNumberChange={handlePartNumberChange}
              getPartNumberOptions={getPartNumberOptions}
              isSelectionView={true}
              handleSplitRow={handleSplitRow}
              handleRemoveSplitRow={handleRemoveSplitRow}
              getGroupTotalQuantity={getGroupTotalQuantity}
            />
          </StyledTableContainer>
          
          <NotificationMessage isPartial={isPartialIssue}>
            {isPartialIssue 
              ? 'Warning: Partial items selected. Some requested items or quantities will not be issued.' 
              : 'All requested items selected for issue with full quantities.'}
          </NotificationMessage>
          
          <ModalFooter>
            <CancelButton onClick={handleCloseIssueModal}>
              Cancel
            </CancelButton>
            <ClearButton onClick={handleClearAllSelections}>
              Clear All
            </ClearButton>
            <ActionButton 
              onClick={handleConfirmSelection} 
              disabled={consumablesToIssue.every(item => !item.isSelected)}
              title={consumablesToIssue.every(item => !item.isSelected) ? "Please select at least one item" : "Confirm your selection"}
            >
              Confirm Selection
            </ActionButton>
          </ModalFooter>
        </Box>
      </Modal>
    </>
  );
};

// Apply memo to component before exporting
const MemoizedStoreL1InboxFormBTS = React.memo(StoreL1InboxFormBTS);
export default MemoizedStoreL1InboxFormBTS;